/**
 * Быстрая правка договора без «изменить данные» (право `contract_edit`, **пишет в боевую CRM**):
 * даты проката — кликом по дате, город выдачи — кликом по «МСК» у номера договора (владелец 23.10.26).
 * Уходит только изменённое поле (`patchContract`), версия — прочитанного договора; итог — `WriteOutcome`.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { DatePick } from "@/components/DateTimePick";
import { WriteOutcome } from "@/components/contract/ui";
import { type ContractWriteResult, type RawContract, contractOptionsQuery, patchContract, useContractRefresh, writeError } from "@/lib/contract";
import type { Contract } from "@/lib/dealContract";
import { day, rentDays } from "@/lib/dealStage";
import { plural } from "@/lib/plural";

const chip = "rounded-[var(--radius-ui)] border px-2 py-0.5 text-[12px]";

type Ctx = { c: Contract; raw: RawContract | null; dealId: number; chatId: number | null };

function useQuickWrite({ c, raw, dealId, chatId }: Ctx) {
  const refresh = useContractRefresh(dealId, c.id, chatId);
  const [done, setDone] = useState<ContractWriteResult | null>(null);
  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) => patchContract(c.id, fields, raw!.version, chatId),
    onMutate: () => setDone(null),
    onSuccess: setDone,
    onSettled: refresh,
  });
  const status = (
    <>
      {save.isPending && <div className="text-[12px] text-fg-muted">Записываю в CRM…</div>}
      {save.isError && <div className="whitespace-pre-wrap text-[12px] text-err">{writeError(save.error)}</div>}
      {done && <WriteOutcome r={done} ok="Записано в CRM" onClose={() => setDone(null)} />}
    </>
  );
  return { save, status, busy: save.isPending || save.isError || done != null };
}

/** «Прокат 22.01 – 24.01 · 3 дня»: с правом — две даты-кнопки, выбрал день — сразу в CRM. */
export function RentQuick(ctx: Ctx & { can: boolean }) {
  const { c, raw, can } = ctx;
  const { save, status } = useQuickWrite(ctx);
  const sent = save.isPending ? (save.variables as { rentStart?: string; rentEnd?: string }) : undefined;
  // Версия правки — у прочитанного договора, значит и «было» берём оттуда же.
  const was = { start: String(raw?.dogovor.rentStart ?? "").slice(0, 10), end: String(raw?.dogovor.rentEnd ?? "").slice(0, 10) };
  const start = sent?.rentStart ?? was.start;
  const end = sent?.rentEnd ?? was.end;
  const days = can && raw ? rentDays(start || null, end || null) : rentDays(c.rent.start, c.rent.end);
  const put = (s: string, e: string) => {
    const f: Record<string, string> = {};
    if (s !== was.start) f.rentStart = s;
    if (e !== was.end) f.rentEnd = e;
    if (Object.keys(f).length) save.mutate(f);
  };
  const tail = days ? <span className="text-fg-muted">{` · ${days} ${plural(days, "день", "дня", "дней")}`}</span> : null;
  if (!can || !raw)
    return (
      <div>
        Прокат{" "}
        {c.rent.start || c.rent.end ? (
          <span className="font-medium tabular-nums">
            {day(c.rent.start)} – {day(c.rent.end)}
          </span>
        ) : (
          <span className="text-err">дат нет</span>
        )}
        {tail}
      </div>
    );
  return (
    <div className="flex flex-col gap-0.5" data-annot={`даты проката договора ${c.id}`}>
      <div className="flex flex-wrap items-center gap-x-1.5">
        Прокат
        <DatePick
          label="Начало проката"
          value={start}
          disabled={save.isPending}
          placeholder="начало…"
          onChange={(d) => put(d, end && end >= d ? end : d)}
        />
        <span aria-hidden className="text-fg-muted">–</span>
        <DatePick
          label="Конец проката"
          value={end}
          disabled={save.isPending}
          placeholder="конец…"
          align="right"
          onChange={(d) => put(start && start <= d ? start : d, d)}
        />
        {tail}
      </div>
      {status}
    </div>
  );
}

/** Город выдачи коротко («МСК») у номера договора: с правом — клик, салоны, выбрал — сразу в CRM. */
export function CityQuick(ctx: Ctx & { can: boolean }) {
  const { c, raw, can } = ctx;
  const [open, setOpen] = useState(false);
  const options = useQuery({ ...contractOptionsQuery(), enabled: open });
  const { save, status, busy } = useQuickWrite(ctx);
  const current = raw?.dogovor.locationID != null ? Number(raw.dogovor.locationID) : (c.location_id ?? null);
  const label = c.city_short || c.city;
  const cls = `text-[14px] font-semibold uppercase tracking-wide ${label ? "text-fg-muted" : "text-warn"}`;
  if (!can || !raw)
    return label ? (
      <span className={cls} title={`Город выдачи: ${c.city ?? label}`}>
        {label}
      </span>
    ) : null;
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        disabled={save.isPending}
        onClick={() => setOpen(!open)}
        className={`${cls} hover:text-fg`}
        title={label ? `Город выдачи: ${c.city ?? label} — изменить` : "Указать город выдачи"}
        data-annot={`город выдачи договора ${c.id}`}
      >
        {label ?? "город не указан"} <span className="text-[11px] font-normal normal-case">{open ? "▴" : "▾"}</span>
      </button>
      {(open || busy) && (
        <div className="order-last flex basis-full flex-col gap-1">
          {open && options.isPending && <div className="text-[12px] text-fg-muted">Загружаю салоны…</div>}
          {open && options.isError && <div className="text-[12px] text-err">Салоны не загрузились: {options.error.message}</div>}
          {open && options.data && (
            <div className="flex flex-wrap gap-1">
              {options.data.locations.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  title={l.name}
                  disabled={save.isPending || l.id === current}
                  onClick={() => (setOpen(false), save.mutate({ locationID: l.id }))}
                  className={`${chip} ${l.id === current ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"}`}
                >
                  {l.short || l.name}
                </button>
              ))}
            </div>
          )}
          {status}
        </div>
      )}
    </>
  );
}
